(() => {
  'use strict';

  const config = window.DinashTheme || {};
  const routes = config.routes || {};
  const strings = config.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer:fine)').matches;
  const store = {
    get(key, fallback = []) {
      try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch (_) { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* no-op */ }
    }
  };

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, character => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#039;','"':'&quot;'}[character]));

  function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
    const value = Number(cents || 0);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelimiter = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    const match = format.match(placeholder);
    if (!match) return new Intl.NumberFormat(undefined, { style: 'currency', currency: config.currency || 'USD' }).format(value / 100);
    let result;
    switch (match[1]) {
      case 'amount_no_decimals': result = withDelimiter(value, 0); break;
      case 'amount_with_comma_separator': result = withDelimiter(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': result = withDelimiter(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': result = withDelimiter(value, 2, "'", '.'); break;
      default: result = withDelimiter(value, 2); break;
    }
    return format.replace(placeholder, result).replace(/<[^>]*>/g, '');
  }

  let toastTimer;
  function toast(message) {
    const element = qs('[data-toast]');
    if (!element || !message) return;
    element.textContent = message;
    element.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => element.classList.remove('show'), 2800);
  }

  function lockBody(locked) {
    document.body.classList.toggle('menu-open', locked);
  }

  function setPanel(panel, open, focusSelector) {
    if (!panel) return;
    panel.classList.toggle('open', open);
    panel.setAttribute('aria-hidden', String(!open));
    if (panel.matches('[data-mobile-panel]')) qsa('[data-mobile-open]').forEach(button => button.setAttribute('aria-expanded', String(open)));
    if (panel.matches('[data-search-panel]')) qsa('[data-search-open]').forEach(button => button.setAttribute('aria-expanded', String(open)));
    if (panel.matches('[data-cart-drawer]')) qsa('[data-cart-open]').forEach(button => button.setAttribute('aria-expanded', String(open)));
    if (panel.matches('[data-filter-form]')) qsa('[data-filter-open]').forEach(button => button.setAttribute('aria-expanded', String(open)));
    lockBody(open || Boolean(qs('.mobile-panel.open, .search-panel.open, .cart-drawer.open, .filter-panel.open')));
    if (open && focusSelector) window.setTimeout(() => qs(focusSelector, panel)?.focus(), 80);
  }

  function setupThemeToggle(scope = document) {
    qsa('[data-theme-toggle]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        const next = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
        document.documentElement.dataset.theme = next;
        try { localStorage.setItem('dinash-theme', next); } catch (_) { /* no-op */ }
        syncThemeButtons();
      });
    });
    syncThemeButtons();
  }

  function syncThemeButtons() {
    const dark = document.documentElement.dataset.theme === 'dark';
    qsa('[data-theme-toggle]').forEach(button => {
      button.setAttribute('aria-pressed', String(dark));
      button.setAttribute('aria-label', dark ? 'Switch to light mode' : 'Switch to dark mode');
    });
    qsa('[data-theme-label]').forEach(label => { label.textContent = dark ? 'Light mode' : 'Dark mode'; });
  }

  function setupNavigation(scope = document) {
    qsa('[data-mobile-open]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => setPanel(qs('[data-mobile-panel]'), true, '[data-mobile-close]'));
    });
    qsa('[data-mobile-close]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => setPanel(qs('[data-mobile-panel]'), false));
    });
    qsa('[data-search-open]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => setPanel(qs('[data-search-panel]'), true, 'input[type="search"]'));
    });
    qsa('[data-search-close]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => setPanel(qs('[data-search-panel]'), false));
    });
  }

  function setupScrollEffects() {
    if (document.documentElement.dataset.scrollReady) return;
    document.documentElement.dataset.scrollReady = 'true';
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      qs('[data-site-header]')?.classList.toggle('is-scrolled', y > 42);
      const progress = qs('[data-scroll-progress]');
      if (progress) {
        const scrollable = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        progress.style.transform = `scaleX(${Math.min(1, y / scrollable)})`;
      }
      if (!reducedMotion && y < window.innerHeight * 1.2) qsa('[data-hero-image]').forEach(image => image.style.setProperty('--hero-y', `${Math.min(58, y * .055)}px`));
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  const revealObserver = !reducedMotion && 'IntersectionObserver' in window
    ? new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible', 'in-view');
        revealObserver.unobserve(entry.target);
      }), { threshold: .1, rootMargin: '0px 0px -4% 0px' })
    : null;

  function setupVisualEffects(scope = document) {
    qsa('[data-reveal], .reveal', scope).forEach(element => {
      if (element.dataset.revealReady) return;
      element.dataset.revealReady = 'true';
      const delay = Number(element.dataset.delay || 0);
      element.style.setProperty('--reveal-delay', `${delay}ms`);
      if (revealObserver) revealObserver.observe(element);
      else element.classList.add('is-visible', 'in-view');
    });

    qsa('.v3-spotlight', scope).forEach(section => {
      if (section.dataset.spotlightReady || reducedMotion || !finePointer) return;
      section.dataset.spotlightReady = 'true';
      section.addEventListener('pointermove', event => {
        const box = section.getBoundingClientRect();
        section.style.setProperty('--pointer-x', `${event.clientX - box.left}px`);
        section.style.setProperty('--pointer-y', `${event.clientY - box.top}px`);
      });
    });

    qsa('[data-tilt]', scope).forEach(card => {
      if (card.dataset.tiltReady || reducedMotion || !finePointer) return;
      card.dataset.tiltReady = 'true';
      card.addEventListener('pointermove', event => {
        const box = card.getBoundingClientRect();
        const x = (event.clientX - box.left) / box.width - .5;
        const y = (event.clientY - box.top) / box.height - .5;
        card.style.transform = `perspective(950px) rotateX(${(-y * 3.2).toFixed(2)}deg) rotateY(${(x * 4.2).toFixed(2)}deg) translateY(-2px)`;
      });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });

    qsa('video[autoplay]', scope).forEach(video => {
      if (video.dataset.videoReady) return;
      video.dataset.videoReady = 'true';
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.play().catch(() => {});
          else entry.target.pause();
        }), { rootMargin: '160px' });
        observer.observe(video);
      }
    });
  }

  function setupRails(scope = document) {
    qsa('[data-rail]', scope).forEach(rail => {
      if (rail.dataset.railReady) return;
      rail.dataset.railReady = 'true';
      const section = rail.closest('section') || document;
      qs('[data-rail-prev]', section)?.addEventListener('click', () => rail.scrollBy({ left: -Math.min(rail.clientWidth * .78, 620), behavior: reducedMotion ? 'auto' : 'smooth' }));
      qs('[data-rail-next]', section)?.addEventListener('click', () => rail.scrollBy({ left: Math.min(rail.clientWidth * .78, 620), behavior: reducedMotion ? 'auto' : 'smooth' }));
    });
  }

  function setupComparisons(scope = document) {
    qsa('[data-compare]', scope).forEach(compare => {
      if (compare.dataset.compareReady) return;
      compare.dataset.compareReady = 'true';
      const range = qs('[data-compare-range]', compare);
      const after = qs('[data-compare-after]', compare);
      const afterImage = after?.querySelector('img');
      const handle = qs('[data-compare-handle]', compare);
      const update = () => {
        const value = Number(range?.value || 50);
        if (after) after.style.width = `${value}%`;
        if (handle) handle.style.left = `${value}%`;
        if (afterImage) afterImage.style.width = `${10000 / Math.max(1, value)}%`;
      };
      range?.addEventListener('input', update);
      update();
    });
  }

  function setupInstagram(scope = document) {
    qsa('[data-insta-account]', scope).forEach(tab => {
      if (tab.dataset.ready) return;
      tab.dataset.ready = 'true';
      tab.addEventListener('click', () => {
        const section = tab.closest('section') || document;
        qsa('[data-insta-account]', section).forEach(item => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
        });
        const handle = tab.dataset.handle || '';
        const url = tab.dataset.url || `https://www.instagram.com/${handle}`;
        const grid = qs('[data-insta-grid]', section);
        grid?.classList.add('is-switching');
        window.setTimeout(() => {
          const follow = qs('[data-insta-follow]', section);
          if (follow) follow.href = url;
          const followHandle = qs('[data-insta-follow-handle]', section);
          if (followHandle) followHandle.textContent = handle;
          qsa('[data-insta-handle]', section).forEach(node => { node.textContent = handle; });
          grid?.classList.remove('is-switching');
        }, reducedMotion ? 0 : 150);
      });
    });
  }

  function setupAccordions(scope = document) {
    qsa('.accordion-button', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        const item = button.closest('.accordion-item');
        const open = !item?.classList.contains('open');
        item?.classList.toggle('open', open);
        button.setAttribute('aria-expanded', String(open));
      });
    });
  }

  function setupQuantity(scope = document) {
    qsa('[data-quantity-control]', scope).forEach(control => {
      if (control.dataset.ready) return;
      control.dataset.ready = 'true';
      control.addEventListener('click', event => {
        const productButton = event.target.closest('[data-qty-change]');
        const cartButton = event.target.closest('[data-cart-qty]');
        const button = productButton || cartButton;
        if (!button) return;
        const input = qs('input', control);
        if (!input) return;
        const minimum = Number(input.min || (cartButton ? 0 : 1));
        const delta = Number(productButton?.dataset.qtyChange || cartButton?.dataset.cartQty || 0);
        input.value = String(Math.max(minimum, Number(input.value || minimum) + delta));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function setupGiftCardRecipients(scope = document) {
    qsa('[data-gift-card-recipient]', scope).forEach(container => {
      if (container.dataset.ready) return;
      container.dataset.ready = 'true';
      const checkbox = qs('[data-recipient-checkbox]', container);
      const fields = qs('[data-recipient-fields]', container);
      const controlled = qsa('[data-recipient-field]', container);
      const required = qsa('[data-recipient-required]', container);
      const offset = qs('[data-recipient-offset]', container);
      const sendDate = qs('[data-recipient-date]', container);
      const message = qs('[data-recipient-message]', container);
      const counter = qs('[data-recipient-counter]', container);

      const localIsoDate = date => {
        const offsetMilliseconds = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - offsetMilliseconds).toISOString().slice(0, 10);
      };

      if (sendDate) {
        const today = new Date();
        const latest = new Date(today);
        latest.setDate(latest.getDate() + 90);
        sendDate.min = localIsoDate(today);
        sendDate.max = localIsoDate(latest);
      }

      const updateCounter = () => {
        if (!message || !counter) return;
        counter.textContent = `${Math.max(0, 200 - message.value.length)} ${strings.charactersRemaining || 'characters remaining'}`;
      };

      const synchronize = () => {
        const enabled = Boolean(checkbox?.checked);
        if (fields) fields.hidden = !enabled;
        controlled.forEach(input => { input.disabled = !enabled; });
        required.forEach(input => { input.required = enabled; });
        if (offset) offset.value = String(new Date().getTimezoneOffset());
        updateCounter();
      };

      checkbox?.addEventListener('change', synchronize);
      message?.addEventListener('input', updateCounter);
      synchronize();
    });
  }

  function setupProduct(scope = document) {
    qsa('[data-product-section]', scope).forEach(section => {
      if (section.dataset.productReady) return;
      section.dataset.productReady = 'true';
      const json = qs('[data-product-json]', section);
      const form = qs('.product-form', section);
      let variants = [];
      let optionController;
      try { variants = JSON.parse(json?.textContent || '[]'); } catch (_) { variants = []; }

      qsa('[data-gallery-thumb]', section).forEach(button => button.addEventListener('click', () => {
        const id = button.dataset.mediaId;
        qsa('[data-gallery-thumb]', section).forEach(item => {
          const active = item === button;
          item.classList.toggle('active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        qsa('[data-media-item]', section).forEach(item => {
          const active = item.dataset.mediaItem === id;
          item.classList.toggle('active', active);
          item.hidden = !active;
          if (!active) item.querySelector('video')?.pause();
        });
      }));

      const selectedValues = () => qsa('[data-option-position]', section).map(group => qs('[data-option-value].active', group)?.dataset.optionValue || '');
      const selectedValueIds = () => qsa('[data-option-position]', section).map(group => qs('[data-option-value].active', group)?.dataset.optionValueId || '').filter(Boolean);

      const syncVariant = variant => {
        const variantInput = qs('[data-variant-id]', form);
        const paymentInput = qs('[data-payment-terms-variant]', section);
        const addButton = qs('[data-add-to-cart]', form);
        const addText = qs('[data-add-to-cart-text]', form);
        const id = variant?.id || '';
        [variantInput, paymentInput].forEach(input => {
          if (!input) return;
          input.value = id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        if (!variant) {
          if (addButton) addButton.disabled = true;
          if (addText) addText.textContent = strings.unavailable || 'Unavailable';
          return;
        }

        const price = qs('[data-current-price]', section);
        if (price && Number.isFinite(Number(variant.price))) price.textContent = formatMoney(variant.price);
        const compare = qs('[data-compare-price]', section);
        if (compare) {
          const comparePrice = Number(variant.compare_at_price || 0);
          compare.hidden = !(comparePrice > Number(variant.price || 0));
          compare.textContent = comparePrice ? formatMoney(comparePrice) : '';
        }
        if (addButton) addButton.disabled = !variant.available;
        if (addText) addText.textContent = variant.available ? (strings.addToCart || 'Add to bag') : (strings.soldOut || 'Sold out');
        const mediaId = variant.featured_media?.id || variant.mediaId;
        if (mediaId) qs(`[data-gallery-thumb][data-media-id="${mediaId}"]`, section)?.click();
      };

      const updateLocalVariant = () => {
        const values = selectedValues();
        const variant = variants.find(candidate => candidate.options.every((option, index) => option === values[index]));
        syncVariant(variant);
        if (!variant) return;
        const url = new URL(window.location.href);
        url.searchParams.delete('option_values');
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      };

      qsa('[data-option-value]', section).forEach(button => button.addEventListener('click', async () => {
        const group = button.closest('[data-option-position]');
        qsa('[data-option-value]', group).forEach(item => {
          const active = item === button;
          item.classList.toggle('active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        const selectedLabel = qs('[data-selected-option]', group);
        if (selectedLabel) selectedLabel.textContent = button.dataset.optionValue;

        const immediateVariantId = Number(button.dataset.optionVariantId || 0);
        if (immediateVariantId) {
          syncVariant({
            id: immediateVariantId,
            price: Number(button.dataset.optionVariantPrice || 0),
            compare_at_price: Number(button.dataset.optionVariantComparePrice || 0),
            available: button.dataset.optionVariantAvailable === 'true',
            mediaId: button.dataset.optionMediaId || ''
          });
        }

        const ids = selectedValueIds();
        if (!ids.length) { updateLocalVariant(); return; }

        const sectionId = section.dataset.sectionId;
        const productUrl = button.dataset.optionProductUrl || section.dataset.productUrl || window.location.pathname;
        const requestUrl = new URL(productUrl, window.location.origin);
        requestUrl.searchParams.set('section_id', sectionId);
        requestUrl.searchParams.set('option_values', ids.join(','));
        optionController?.abort();
        optionController = new AbortController();
        section.classList.add('is-loading');
        section.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(requestUrl, {
            signal: optionController.signal,
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });
          if (!response.ok) throw new Error('Variant request failed');
          const responseText = await response.text();
          const parsed = new DOMParser().parseFromString(responseText, 'text/html');
          const replacement = qs('[data-product-section]', parsed);
          if (!replacement) throw new Error('Variant response was incomplete');

          const focusValueId = button.dataset.optionValueId;
          section.replaceWith(replacement);
          initialize(replacement);

          const nextProductUrl = replacement.dataset.productUrl || productUrl;
          const pageUrl = new URL(nextProductUrl, window.location.origin);
          const selectedVariantId = qs('[data-variant-id]', replacement)?.value;
          if (selectedVariantId) pageUrl.searchParams.set('variant', selectedVariantId);
          else pageUrl.searchParams.set('option_values', ids.join(','));
          history.replaceState({}, '', pageUrl);

          if (focusValueId) {
            qsa('[data-option-value-id]', replacement).find(item => item.dataset.optionValueId === focusValueId)?.focus();
          }
        } catch (error) {
          if (error.name !== 'AbortError') updateLocalVariant();
        } finally {
          if (section.isConnected) {
            section.classList.remove('is-loading');
            section.removeAttribute('aria-busy');
          }
        }
      }));
    });
  }

  async function fetchCart() {
    const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  }

  function imageUrlWithWidth(value, width) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (_) {
      return `${value}${value.includes('?') ? '&' : '?'}width=${width}`;
    }
  }

  function renderCartProperties(item) {
    return Object.entries(item.properties || {})
      .filter(([key, value]) => value != null && String(value).trim() !== '' && !key.startsWith('_'))
      .map(([key, value]) => {
        const safeKey = escapeHtml(key);
        const safeValue = escapeHtml(value);
        if (String(value).includes('/uploads/')) {
          const fileName = escapeHtml(String(value).split('/').pop());
          return `<small class="cart-drawer__property">${safeKey}: <a href="${safeValue}" target="_blank" rel="noopener">${fileName}</a></small>`;
        }
        return `<small class="cart-drawer__property">${safeKey}: ${safeValue}</small>`;
      }).join('');
  }

  function renderCartDrawer(cart) {
    qsa('[data-cart-count]').forEach(node => { node.textContent = cart.item_count; });
    const mount = qs('[data-cart-drawer-items]');
    const footer = qs('[data-cart-drawer-footer]');
    if (!mount) return;
    if (!cart.items.length) {
      mount.innerHTML = `<div class="empty-state cart-drawer__empty"><h3 class="display-card">${escapeHtml(config.cartEmptyTitle || 'Your bag is waiting.')}</h3><p class="body-copy">${escapeHtml(strings.cartEmptyMessage || 'Discover premium extensions selected for a natural blend.')}</p><a class="btn btn-dark" href="${escapeHtml(routes.allProducts || '/collections/all')}">${escapeHtml(strings.continueShopping || 'Continue shopping')}</a></div>`;
      if (footer) footer.hidden = true;
      return;
    }
    mount.innerHTML = cart.items.map((item, index) => {
      const image = item.image ? `<img src="${escapeHtml(imageUrlWithWidth(item.image, 240))}" alt="${escapeHtml(item.product_title)}" width="120" height="150">` : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<span>${escapeHtml(item.variant_title)}</span>` : '';
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.name ? `<span>${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</span>` : '';
      return `<article class="cart-drawer__item" data-cart-line="${index + 1}"><a href="${escapeHtml(item.url)}" class="cart-drawer__image">${image}</a><div class="cart-drawer__copy"><a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a>${variant}${sellingPlan}${renderCartProperties(item)}<span>${formatMoney(item.final_line_price)}</span><div class="cart-drawer__quantity"><button type="button" data-drawer-change="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="${escapeHtml(strings.decreaseQuantity || 'Decrease quantity')}">−</button><span>${item.quantity}</span><button type="button" data-drawer-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="${escapeHtml(strings.increaseQuantity || 'Increase quantity')}">+</button></div><button type="button" data-cart-remove data-line="${index + 1}">${escapeHtml(strings.remove || 'Remove')}</button></div></article>`;
    }).join('');
    if (footer) footer.hidden = false;
    const total = qs('[data-cart-drawer-total]');
    if (total) total.textContent = formatMoney(cart.total_price);
  }

  async function refreshCart(open = false) {
    try {
      const cart = await fetchCart();
      renderCartDrawer(cart);
      if (open && config.settings?.cartDrawer) setPanel(qs('[data-cart-drawer]'), true, '[data-cart-drawer-close]');
      return cart;
    } catch (_) {
      toast(strings.cartError || 'We could not update your bag.');
      return null;
    }
  }

  async function addFormToCart(form, submitter) {
    const submit = submitter || qs('[data-add-to-cart], [type="submit"]', form);
    const original = submit?.innerHTML;
    if (submit) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }
    try {
      const response = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || strings.cartError || 'Unable to add item');
      toast(strings.addedToCart || 'Added to your bag.');
      await refreshCart(true);
      const message = qs('[data-product-form-message]', form);
      if (message) message.textContent = strings.addedToCart || 'Added to your bag.';
    } catch (error) {
      const message = qs('[data-product-form-message]', form);
      if (message) message.textContent = error.message;
      toast(error.message);
    } finally {
      if (submit) { submit.disabled = false; submit.removeAttribute('aria-busy'); if (original) submit.innerHTML = original; }
    }
  }

  function setupCart(scope = document) {
    qsa('[data-cart-open]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => refreshCart(true));
    });
    qsa('[data-cart-drawer-close]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => setPanel(qs('[data-cart-drawer]'), false));
    });
  }

  async function changeCartLine(line, quantity) {
    try {
      const response = await fetch(`${routes.cartChange}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || strings.cartError);
      renderCartDrawer(cart);
    } catch (error) { toast(error.message || strings.cartError); }
  }

  function wishlistItems() { return store.get('dinash-wishlist-v1', []); }
  function saveWishlist(items) { store.set('dinash-wishlist-v1', items); updateWishlistUI(); }
  function updateWishlistUI() {
    const items = wishlistItems();
    qsa('[data-wishlist-count]').forEach(node => { node.textContent = items.length; });
    qsa('[data-wishlist]').forEach(button => {
      const active = items.some(item => item.handle === button.dataset.handle);
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    renderWishlistPage(items);
  }

  function toggleWishlist(button) {
    const handle = button.dataset.handle;
    if (!handle) return;
    let items = wishlistItems();
    const exists = items.some(item => item.handle === handle);
    if (exists) items = items.filter(item => item.handle !== handle);
    else items.push({ handle, title: button.dataset.product || '', url: button.dataset.url || `/products/${handle}`, price: Number(button.dataset.price || 0), image: button.dataset.image || '', variantId: button.dataset.variantId || '' });
    saveWishlist(items);
    toast(exists ? (strings.wishlistRemoved || 'Removed from your wishlist.') : (strings.wishlistAdded || 'Saved to your wishlist.'));
  }

  function renderWishlistPage(items = wishlistItems()) {
    const grid = qs('[data-wishlist-grid]');
    const empty = qs('[data-wishlist-empty]');
    if (!grid) return;
    grid.hidden = items.length === 0;
    if (empty) empty.hidden = items.length > 0;
    grid.innerHTML = items.map(item => `<article class="product-card"><div class="product-media"><a href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}</a><button class="wishlist-button active" type="button" aria-pressed="true" data-wishlist data-handle="${escapeHtml(item.handle)}" data-product="${escapeHtml(item.title)}" data-url="${escapeHtml(item.url)}" data-price="${item.price}" data-image="${escapeHtml(item.image)}" data-variant-id="${escapeHtml(item.variantId)}" aria-label="Remove ${escapeHtml(item.title)} from wishlist"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"/></svg></button>${item.variantId ? `<form class="quick-add-form" method="post" action="${escapeHtml(routes.cartAdd)}"><input type="hidden" name="id" value="${escapeHtml(item.variantId)}"><input type="hidden" name="quantity" value="1"><button class="quick-add btn btn-dark btn-full" type="submit">Quick add</button></form>` : ''}</div><div class="product-info"><h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a></h3><div class="product-meta"><span>Saved style</span><span class="product-price">${formatMoney(item.price)}</span></div></div></article>`).join('');
  }

  function setupWishlist() { updateWishlistUI(); }

  function setupFilters(scope = document) {
    qsa('[data-filter-open]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => setPanel(qs('[data-filter-form]'), true, '[data-filter-close]'));
    });
    qsa('[data-filter-close]', scope).forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => setPanel(qs('[data-filter-form]'), false));
    });
    qsa('[data-sort-by]', scope).forEach(select => {
      if (select.dataset.ready) return;
      select.dataset.ready = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  }

  function setupPredictiveSearch(scope = document) {
    qsa('[data-predictive-search-form]', scope).forEach(form => {
      if (form.dataset.ready) return;
      form.dataset.ready = 'true';
      const input = qs('[data-predictive-search-input]', form);
      const results = qs('[data-predictive-search-results]', form.closest('.search-dialog'));
      let controller;
      let timer;
      input?.addEventListener('input', () => {
        clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < 2) { if (results) results.innerHTML = ''; return; }
        timer = window.setTimeout(async () => {
          controller?.abort();
          controller = new AbortController();
          try {
            const url = `${routes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,collection,article,page&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) throw new Error('Search unavailable');
            const html = await response.text();
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const content = qs('#PredictiveSearchResults', parsed);
            if (results) results.innerHTML = content?.outerHTML || '';
          } catch (error) { if (error.name !== 'AbortError' && results) results.innerHTML = ''; }
        }, 220);
      });
    });
  }

  function setupRecommendations(scope = document) {
    qsa('[data-product-recommendations]', scope).forEach(container => {
      if (container.dataset.ready || container.innerHTML.trim()) return;
      container.dataset.ready = 'true';
      fetch(container.dataset.url).then(response => response.text()).then(text => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendations = qs('[data-product-recommendations]', html);
        if (recommendations?.innerHTML.trim()) {
          container.innerHTML = recommendations.innerHTML;
          initialize(container);
        }
      }).catch(() => {});
    });
  }

  function initialize(scope = document) {
    setupThemeToggle(scope);
    setupNavigation(scope);
    setupVisualEffects(scope);
    setupRails(scope);
    setupComparisons(scope);
    setupInstagram(scope);
    setupAccordions(scope);
    setupQuantity(scope);
    setupGiftCardRecipients(scope);
    setupProduct(scope);
    setupCart(scope);
    setupFilters(scope);
    setupPredictiveSearch(scope);
    setupRecommendations(scope);
    setupWishlist();
  }

  document.addEventListener('submit', event => {
    const form = event.target.closest('.quick-add-form, .product-form');
    if (!form) return;
    const submitter = event.submitter;
    if (submitter?.closest('.shopify-payment-button') || submitter?.classList.contains('shopify-payment-button__button')) return;
    event.preventDefault();
    addFormToCart(form, submitter);
  });

  document.addEventListener('click', event => {
    const wishlistButton = event.target.closest('[data-wishlist]');
    if (wishlistButton) { event.preventDefault(); toggleWishlist(wishlistButton); return; }
    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) { event.preventDefault(); changeCartLine(Number(removeButton.dataset.line), 0); return; }
    const changeButton = event.target.closest('[data-drawer-change]');
    if (changeButton) { event.preventDefault(); changeCartLine(Number(changeButton.dataset.drawerChange), Number(changeButton.dataset.quantity)); }
  });

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    setPanel(qs('[data-mobile-panel]'), false);
    setPanel(qs('[data-search-panel]'), false);
    setPanel(qs('[data-cart-drawer]'), false);
    setPanel(qs('[data-filter-form]'), false);
  });

  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.documentElement.classList.add('js-ready');
  setupScrollEffects();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
})();
